import React from "react";
import {
  Box,
  Heading,
  Text,
  Input,
  HStack,
  VStack,
  Button,
  IconButton,
  Divider,
  Tooltip,
} from "@chakra-ui/react";
import { CloseIcon } from "@chakra-ui/icons";

const SectionBox = ({ title, children }) => (
  <Box
    mt={6}
    p={4}
    border="2px solid"
    borderColor="gray.600"
    borderRadius="md"
    bg="white"
    boxShadow="md"
    textAlign="left"
  >
    <Heading size="md" mb={2}>{title}</Heading>
    <Divider mb={4} />
    {children}
  </Box>
);

const getBorderColor = (original, value) => {
  const hasOriginal = original !== undefined && original !== null && original !== "";
  const hasValue = value !== undefined && value !== null && value !== "";
  if (!hasOriginal && hasValue) return "2px solid green"; // Added
  if (hasOriginal && !hasValue) return "2px solid red";   // Deleted
  if (hasOriginal && hasValue && original !== value) return "2px solid blue"; // Changed
  return "1px solid #E2E8F0"; // Unchanged
};

const getTooltipLabel = (original, value) => {
  const hasOriginal = original !== undefined && original !== null && original !== "";
  const hasValue = value !== undefined && value !== null && value !== "";
  if (!hasOriginal && hasValue) return "Added new value";
  if (hasOriginal && !hasValue) return `Deleted previous value: ${original}`;
  if (hasOriginal && hasValue && original !== value) return `Changed from: ${original}`;
  return "";
};

const AdditionalPhysiciansSection = ({
  additionalPhysicians,
  updatePhysician,
  deletePhysician,
  addPhysician,
  physicianInfo,
  originalData = {},
}) => {
  const originalList = originalData.additionalPhysicians || [];

  const renderInput = (label, field, value, original, onChange) => (
    <Box flex="1">
      <Text fontSize="sm" mb={1}>{label}</Text>
      <Tooltip label={getTooltipLabel(original, value)} isDisabled={!getTooltipLabel(original, value)}>
        <Input
          value={value || ""}
          onChange={onChange}
          border={getBorderColor(original, value)}
        />
      </Tooltip>
    </Box>
  );

  return (
    <SectionBox title="Additional Physicians">
      <Text fontSize="sm" color="gray.600" mb={3}>
        Add additional physicians associated with this agreement.
      </Text>

      {additionalPhysicians.map((physician, index) => {
        const original = originalList[index] || {};

        return (
          <Box
            key={index}
            position="relative"
            border="1px solid"
            borderColor="gray.500"
            p={4}
            mt={3}
            borderRadius="md"
          >
            <IconButton
              icon={<CloseIcon />}
              aria-label="Remove physician"
              size="sm"
              position="absolute"
              top="-10px"
              right="-10px"
              zIndex="10"
              bg="#D62828"
              color="white"
              boxShadow="md"
              _hover={{ bg: "gray.100" }}
              onClick={() => deletePhysician(index)}
            />

            <VStack spacing={3} align="stretch">
              <HStack spacing={4}>
                {renderInput("First Name", "firstName", physician.firstName, original.firstName, (e) => updatePhysician(index, "firstName", e.target.value))}
                {renderInput("Last Name", "lastName", physician.lastName, original.lastName, (e) => updatePhysician(index, "lastName", e.target.value))}
              </HStack>

              {renderInput("Specialty", "specialty", physician.specialty, original.specialty, (e) => updatePhysician(index, "specialty", e.target.value))}

              <HStack spacing={4}>
                {renderInput("NPI", "npi", physician.npi, original.npi, (e) => updatePhysician(index, "npi", e.target.value))}
                {renderInput("TIN", "tin", physician.tin, original.tin, (e) => updatePhysician(index, "tin", e.target.value))}
                {renderInput("PTAN", "ptan", physician.ptan, original.ptan, (e) => updatePhysician(index, "ptan", e.target.value))}
              </HStack>

              <HStack spacing={4}>
                {renderInput("Fax", "fax", physician.fax, original.fax, (e) => updatePhysician(index, "fax", e.target.value))}
              </HStack>

              {renderInput("Street Address", "street", physician.street, original.street, (e) => updatePhysician(index, "street", e.target.value))}

              <HStack spacing={4}>
                {renderInput("City", "city", physician.city, original.city, (e) => updatePhysician(index, "city", e.target.value))}
                {renderInput("State", "state", physician.state, original.state, (e) => updatePhysician(index, "state", e.target.value))}
                {renderInput("Zip Code", "zip", physician.zip, original.zip, (e) => updatePhysician(index, "zip", e.target.value))}
              </HStack>
            </VStack>

            <Button
              mt={2}
              size="sm"
              onClick={() => {
                updatePhysician(index, "street", physicianInfo.street);
                updatePhysician(index, "city", physicianInfo.city);
                updatePhysician(index, "state", physicianInfo.state);
                updatePhysician(index, "zip", physicianInfo.zip);
              }}
            >
              Same as Primary Physician Address
            </Button>
          </Box>
        );
      })}

      {additionalPhysicians.length < 50 && (
        <Button
          mt={3}
          bg="#2A9D8F"
          color="white"
          _hover={{ bg: "#21867a" }}
          onClick={addPhysician}
        >
          Add Another Physician
        </Button>
      )}
    </SectionBox>
  );
};

export default AdditionalPhysiciansSection;